/**
 * SessionDetailPanel Component
 *
 * Right panel showing details for the selected import session.
 * Active sessions show progress + continue/delete actions.
 * Complete sessions show stat boxes (read-only summary).
 */

import { useNavigate } from 'react-router-dom';
import {
  CheckCircleOutline as CheckIcon,
  Delete as DeleteIcon,
  ErrorOutline as ErrorIcon,
  Inbox as InboxIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  PlayArrow as ContinueIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { SessionSummary } from '../types';
import { TERMINAL_STATUSES } from '../types';

// =============================================================================
// Helpers
// =============================================================================

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function isComplete(session: SessionSummary): boolean {
  return TERMINAL_STATUSES.includes(
    session.status as (typeof TERMINAL_STATUSES)[number],
  );
}

// =============================================================================
// Stat Box
// =============================================================================

interface StatBoxProps {
  label: string;
  value: number;
  color?: string;
  icon: React.ReactNode;
}

const StatBox: React.FC<StatBoxProps> = ({ label, value, color, icon }) => {
  const theme = useTheme();
  const resolvedColor = color
    ? (color.split('.').reduce<unknown>(
        (obj, key) => (obj as Record<string, unknown>)?.[key],
        theme.palette,
      ) as string)
    : theme.palette.text.primary;

  return (
    <Box
      sx={{
        p: 2,
        textAlign: 'center',
        flex: 1,
        minWidth: 110,
        borderRadius: 2,
        bgcolor: alpha(resolvedColor, 0.06),
        border: '1px solid',
        borderColor: alpha(resolvedColor, 0.12),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mb: 0.5,
          color: resolvedColor,
          opacity: 0.7,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h4"
        fontWeight={700}
        sx={{ color: resolvedColor, lineHeight: 1.2 }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 500 }}
      >
        {label}
      </Typography>
    </Box>
  );
};

// =============================================================================
// Component
// =============================================================================

interface SessionDetailPanelProps {
  session: SessionSummary | undefined;
  onDelete: (id: string) => Promise<void>;
  isDeleting: boolean;
}

export const SessionDetailPanel: React.FC<SessionDetailPanelProps> = ({
  session,
  onDelete,
  isDeleting,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  if (!session) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.disabled',
          gap: 1,
        }}
      >
        <InboxIcon sx={{ fontSize: 40, opacity: 0.4 }} />
        <Typography variant="body2">Select a session to view details</Typography>
      </Box>
    );
  }

  const complete = isComplete(session);
  const imported =
    (session.processed_patients ?? 0) - (session.failed_patients ?? 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header bar */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.default, 0.5),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {formatDate(session.created_at)}
          </Typography>
          <Chip
            label={statusLabel(session.status)}
            color={
              session.status === 'completed'
                ? 'success'
                : session.status === 'failed' || session.status === 'cancelled'
                  ? 'error'
                  : 'info'
            }
            size="small"
          />
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ ml: 'auto', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}
          >
            {session.file_type}
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {/* Active session content */}
        {!complete && (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<ContinueIcon />}
              onClick={() =>
                navigate(`/patients/import/${session.session_id}`)
              }
            >
              Continue Import
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => onDelete(session.session_id)}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </Box>
        )}

        {/* Complete session content — read-only summary */}
        {complete && (
          <>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <StatBox
                label="Total"
                value={session.total_patients}
                icon={<PeopleIcon sx={{ fontSize: 20 }} />}
              />
              <StatBox
                label="Imported"
                value={imported}
                color="success.main"
                icon={<CheckIcon sx={{ fontSize: 20 }} />}
              />
              <StatBox
                label="Failed"
                value={session.failed_patients}
                color={session.failed_patients > 0 ? 'error.main' : undefined}
                icon={<ErrorIcon sx={{ fontSize: 20 }} />}
              />
              <StatBox
                label="New Patients"
                value={session.new_patients}
                color="info.main"
                icon={<PersonAddIcon sx={{ fontSize: 20 }} />}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={() =>
                  navigate(`/patients/import/${session.session_id}`)
                }
              >
                View Details
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => onDelete(session.session_id)}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
